import { useEffect, useRef, useState } from "react";
import Lobby from "../lobby/Lobby";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import { MeetingRoomHeroLayout } from "../layout/MeetingRoomHeroLayout";
import { isIE, isMobileDevice, safeSetTimeout, ServerTimeHelper, translate } from "@kaltura/mediaspace-shared-utils";
import { MeetingRoom, MeetingRoomHelper, MeetingRoomType } from "@kaltura/mediaspace-shared-meeting-room";
import { HeroProps } from "../HeroProps";
import MeetingButton from "./MeetingButton";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { getPropsToPass, useThumbnailSizing } from "../helpers";
import { MediaLobbyDataContainer } from "../lobby/lobby-data/LobbyData";
import { useCncReactions } from "@mediaspace/features/cnc";
import { KalturaMeetingScheduleEvent } from "kaltura-typescript-client/api/types/KalturaMeetingScheduleEvent";
import { HeroLayout, HeroLayoutMode } from "@kaltura/mediaspace-shared-layout";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

export interface MeetingRoomHeroProps extends HeroProps {
    scheduleEvent?: KalturaMeetingScheduleEvent;
    isAdmin: boolean;
    isPresenter?: boolean;
    roomUrl: string;
    // Open the NewRow room in a new tab (instead of iframing it in-place) - required for mobile app to work
    openInNewTab?: boolean;
    roomStatusInterval?: number; // used to update room status every x milliseconds, only passed for moderators
    safariSupport?: boolean; // allow joining newrow room from Safari using the app (relevant only to legacy rooms)
    mobileSupport?: boolean; // allow joining newrow room from mobile using the app (relevant only to legacy rooms)
    isAdvancedTemplateRoom?: boolean; // is using advanced room templates (supports mobile natively)
    joinButtonType?: "static" | "dynamic" | undefined;
    launchNow?: boolean; // if true and user is admin, the room will open immediately
    fullSize?: boolean; // if true, the meeting room takes the whole screen size except for the sidebar
}

enum Screens {
    LOBBY_ROOM_CLOSED,
    LOBBY_ROOM_OPEN,
    SHOW_ROOM,
}

/**
 * wrapper component for meeting room
 */
const MeetingRoomHero = (props: MeetingRoomHeroProps) => {
    const {
        isAdmin,
        isPresenter = false,
        schedulingData,
        scheduleEvent,
        entry,
        roomUrl,
        openInNewTab: openInNewTabProp,
        roomStatusInterval,
        safariSupport = false,
        mobileSupport = false,
        isAdvancedTemplateRoom = false,
        joinButtonType = "dynamic",
        launchNow = false,
        actions = [],
        thumbnailUrl,
        fullSize,
        isReadOnly = false,
    } = props;

    const config = useConfig();
    const serviceUrl = config?.application?.serviceUrl;

    const STREAM_POLL_INTERVAL = 10000;
    const ONE_HOUR = 3600000; /* 60*60*1000 */
    const TWELVE_HOURS = 43200000; /* 60*60*1000*12 */

    // Safari is guaranteed to be supported by rooms with advanced templates
    const isNewrowSupported = MeetingRoomHelper.isNewrowSupportedInCurrentBrowser(
        safariSupport || isAdvancedTemplateRoom
    );

    const defaultShowScreen =
        joinButtonType === "static" && !isAdmin ? Screens.LOBBY_ROOM_OPEN : Screens.LOBBY_ROOM_CLOSED;
    const [showScreen, setShowScreen] = useState(defaultShowScreen);
    const [actionsToPass, setActionsToPass] = useState(actions);
    const [statusLabel, setStatusLabel] = useState<string>("");

    /**
     * autoEnter only if user wasn't in room before
     */
    const shouldAutoEnterRoom = useRef(true);

    const pollingInterval = useRef(0);

    /**
     * check room live-status
     * @return Promise<Boolean>
     */
    const pollRoom = () => {
        console.info(">> polling room... ");
        return MeetingRoomHelper
            .pollRoom(entry.id, isReadOnly)
            .then((isLive) => {
                console.info(">> room is " + (isLive ? "open" : "close"));
                setShowScreen(isLive ? Screens.LOBBY_ROOM_OPEN : Screens.LOBBY_ROOM_CLOSED);
                return Promise.resolve(isLive);
            })
            .catch((err) => {
                console.info(">> room is closed");
                console.log(err);
                setShowScreen(Screens.LOBBY_ROOM_CLOSED);
                return Promise.resolve(false);
            });
    };

    /**
     * logic of what to show when (room vs lobby), expected to only run once
     * @param serverNow
     */
    const decideWhatToDoPerSchedule = (serverNow: number) => {
        const now = serverNow ? serverNow : Date.now();
        const prestartInMs = scheduleEvent && scheduleEvent.preStartTime ? scheduleEvent.preStartTime * 1000 : 0;

        // if after scheduling window - event should be over but may still be playing - poll
        if (schedulingData && schedulingData.to < now) {
            console.info(">> time is over");
            pollRoom().then((isLive) => {
                if (isLive) {
                    if (shouldAutoEnterRoom.current) {
                        openMeeting();
                    }
                }
            });
        }

        // if more than 12 hours before scheduling window, show hero section and do nothing.
        else if (schedulingData && schedulingData.from - prestartInMs - now > TWELVE_HOURS) {
            console.info(">> it is not yet time, more than 12 hours before scheduling window");
            setShowScreen(Screens.LOBBY_ROOM_CLOSED);
        }

        // if more than 1 hour before scheduling window, show hero section and set timeout to refresh in an hour
        else if (schedulingData && schedulingData.from - prestartInMs - now > ONE_HOUR) {
            console.info(">> it is not yet time");
            const n = Math.floor((schedulingData.from - prestartInMs - now) / ONE_HOUR);
            console.log("will reload in " + n + " hours");
            safeSetTimeout(
                () => {
                    window.location.reload();
                },
                n * ONE_HOUR
            );
            setShowScreen(Screens.LOBBY_ROOM_CLOSED);
        }

        // if before scheduling window (and prestart), show hero section
        else if (schedulingData && schedulingData.from - prestartInMs > now) {
            console.info(">> it is not yet time");
            // set timeout to start polling the room when scheduling window opens (consider prestart)
            const timeToStartPolling = schedulingData.from - prestartInMs - now;
            console.info("will poll in " + timeToStartPolling / 1000 + " secs");
            safeSetTimeout(
                () => {
                    pollRoom().then((isLive) => {
                        if (isLive) {
                            if (shouldAutoEnterRoom.current) {
                                openMeeting();
                            }
                        }
                        else {
                            startPolling();
                        }
                    });
                },
                timeToStartPolling
            );
            setShowScreen(Screens.LOBBY_ROOM_CLOSED);
        }

        // if inside scheduling window
        else if (schedulingData && schedulingData.from - prestartInMs < now && schedulingData.to > now) {
            console.info(">> the time is now");
            // poll room
            pollRoom().then((result) => {
                if (result) {
                    if (shouldAutoEnterRoom.current) {
                        openMeeting();
                    }
                }
                else {
                    // keep polling
                    startPolling();
                }
            });
        }
    };

    const startPolling = () => {
        window.clearInterval(pollingInterval.current);
        pollingInterval.current = window.setInterval(
            () => {
                pollRoom().then((isLive) => {
                    if (isLive) {
                        // no need to keep polling if stream is live
                        window.clearInterval(pollingInterval.current);
                        if (shouldAutoEnterRoom.current) {
                            openMeeting();
                        }
                    }
                });
            },
            STREAM_POLL_INTERVAL
        );
    };

    const decideWhatToDo = useEventHandler(() => {
        if (isAdmin && launchNow) {
            if (shouldAutoEnterRoom.current) {
                openMeeting();
            }
        }
        else if (scheduleEvent && !isAdmin && !isPresenter) {
            decideWhatToDoPerSchedule(ServerTimeHelper.getUpdatedTime());
        }
        else {
            pollRoom().then((isLive) => {
                if (isLive) {
                    if (shouldAutoEnterRoom.current) {
                        openMeeting();
                    }
                }
                else {
                    // start polling
                    startPolling();
                }
            });
        }
    });

    const handleSessionEnded = (endedForAll: boolean) => {
        if (joinButtonType === "static") {
            return;
        }

        if (roomStatusInterval) {
            // in case this was a moderator, stop updating room status
            window.clearInterval(pollingInterval.current);
            // mark the room as closed
            if (endedForAll) {
                MeetingRoomHelper.clearRoomStatus(MeetingRoomType.entry, entry.id, isReadOnly);
            }
        }
        setShowScreen(Screens.LOBBY_ROOM_CLOSED);
        shouldAutoEnterRoom.current = false;
        decideWhatToDo();
    };

    // get time from server, then init accordingly
    useEffect(() => {
        if (joinButtonType === "static") {
            return;
        }

        // get server time, then decide what to do
        ServerTimeHelper
            .getServerTime(serviceUrl)
            .then((value) => {
                ServerTimeHelper.setTimeForDiff(value);
                decideWhatToDo();
            })
            .catch((err) => {
                console.info(">> failed getting system time, using local", err);
                ServerTimeHelper.setTimeForDiff(Date.now());
                decideWhatToDo();
            });

        return () => {
            window.clearInterval(pollingInterval.current);
        };
    }, [decideWhatToDo, serviceUrl, joinButtonType]);

    const openInNewTab = openInNewTabProp && !isAdvancedTemplateRoom;

    const openMeeting = () => {
        if (openInNewTab) {
            window.open(roomUrl, "_blank")?.focus();
        }
        else {
            setupStatusInterval();
            setShowScreen(Screens.SHOW_ROOM);
        }
    };

    /**
     * open a non-live meeting room
     */
    const startMeeting = () => {
        if (!openInNewTab) {
            // stop polling, we are opening the room
            window.clearInterval(pollingInterval.current);
        }
        openMeeting();
    };

    /**
     * ping the status server every x milliseconds
     */
    const setupStatusInterval = () => {
        if (!roomStatusInterval || joinButtonType === "static") {
            // if we didn't get roomStatusInterval prop, this is not a moderator and shouldn't update the room status
            // if the joinButtonType is static, we shouldn't update the room status
            return;
        }
        window.clearInterval(pollingInterval.current);
        pollingInterval.current = window.setInterval(
            () => {
                MeetingRoomHelper.updateRoomStatus(MeetingRoomType.entry, entry.id, isReadOnly);
            },
            roomStatusInterval
        );
    };

    useEffect(() => {
        const showAdminButton = isAdmin && showScreen === Screens.LOBBY_ROOM_CLOSED;
        const roomIsOpen = showScreen === Screens.LOBBY_ROOM_OPEN && isNewrowSupported;
        const showAttendeeButton = roomIsOpen && (!isMobileDevice || mobileSupport || isAdvancedTemplateRoom) && !isIE;
        const inSchedulingRange =
            !!schedulingData?.to
            && !!schedulingData?.to
            && ServerTimeHelper.getUpdatedTime() > schedulingData?.from
            && ServerTimeHelper.getUpdatedTime() < schedulingData?.to;

        const actionsToShow = [];

        if (showAdminButton || showAttendeeButton) {
            actionsToShow.push({
                name: "meeting",
                component: MeetingButton,
                props: {
                    isAdmin,
                    mobileSupport,
                    isAdvancedTemplateRoom,
                    isNewrowSupported,
                    startMeeting,
                    openMeeting,
                },
            });
        }

        const { statusLabel, actionsToPass } = getPropsToPass(entry, schedulingData, actions, isReadOnly);
        setActionsToPass([...actionsToShow, ...actionsToPass]);

        setStatusLabel(inSchedulingRange && roomIsOpen ? translate("Live Now") : statusLabel);
    }, [showScreen]);

    const kmeNotSupported = (isMobileDevice && !mobileSupport && !isAdvancedTemplateRoom) || !isNewrowSupported;

    const sizedThumbnailUrl = useThumbnailSizing(thumbnailUrl);

    const { startReactions, endReactions } = useCncReactions();

    const isShowingRoom = showScreen === Screens.SHOW_ROOM;
    useEffect(() => {
        if (isShowingRoom) {
            startReactions();
        }
        else {
            endReactions();
        }
    }, [isShowingRoom, startReactions, endReactions]);

    const lobby = (
        <Lobby
            thumbnailUrl={sizedThumbnailUrl}
            statusLabel={statusLabel}
            title={entry.name}
            schedulingData={schedulingData}
            actions={actionsToPass}
            data={<MediaLobbyDataContainer {...props} />}
            kmeNotSupported={kmeNotSupported}
        />
    );

    const room = <MeetingRoom roomUrl={roomUrl} onSessionEnd={handleSessionEnded} />;

    if (fullSize) {
        return (
            <HeroLayout mode={HeroLayoutMode.FullSize} allowBorderRadius={false}>
                {isShowingRoom ? room : lobby}
            </HeroLayout>
        );
    }

    return (
        <>
            {!isShowingRoom && (
                <CommonHeroLayout collapsedHero={props.collapse} allowBorderRadius={!isReadOnly}>
                    {lobby}
                </CommonHeroLayout>
            )}

            {isShowingRoom && (
                <MeetingRoomHeroLayout collapsedHero={props.collapse} isAdvancedTemplateRoom={isAdvancedTemplateRoom}>
                    {room}
                </MeetingRoomHeroLayout>
            )}
        </>
    );
};

export default MeetingRoomHero;
